import {
  emitTelemetry,
  flushTelemetry,
  telemetryEnabled,
  telemetryHealth,
  telemetryReportedBytes,
  registerTelemetrySnapshot,
  telemetryId,
  telemetryTimer,
  type XmaxTelemetryEventName,
  type XmaxTelemetryParams,
} from './collector'
import { resolveProbeConfig, type ResolvedXmaxProbeConfig, type XmaxProbeOptions } from './probe-config'
import { RtcStatsAccumulator, type RtcMetricName } from './rtc-stats'
import { getErrorDiagnostics } from '../shared/sdk-error'

export type StartupStage = 'media_prepare' | 'session_api' | 'rtc_prepare' | 'camera_capture' | 'camera_audio' | 'rtc_join' | 'camera_ready' | 'publish' | 'outbound_ready' | 'start_send' | 'context_set' | 'remote_bind' | 'wait_first_frame' | 'streaming'
export const STARTUP_TRACE = Symbol('xmaxStartupTrace')
export type WithStartupTrace = { [STARTUP_TRACE]?: StartupTrace }

/** Stable values accepted by `session.disconnect.attrs.reason_code`. */
export const XMAX_DISCONNECT_REASON_CODES = [
  'client_disconnect',
  'client_background_timeout',
  'client_session_replaced',
  'client_context_disposed',
  'session_inactive',
  'heartbeat_error',
  'remote_output_stalled',
  'remote_unpublished',
  'remote_user_left',
  'remote_first_frame_timeout',
  'local_outbound_zero_sent_frame_rate',
  'local_outbound_track_ended',
  'local_outbound_track_muted',
  'rtc_async_error',
  'rtc_reconnect_timeout',
  'media_prepare_failed',
  'camera_capture_failed',
  'audio_capture_failed',
  'session_api_failed',
  'rtc_prepare_failed',
  'rtc_join_failed',
  'camera_ready_failed',
  'local_publish_failed',
  'local_outbound_not_ready',
  'generation_start_failed',
  'context_update_failed',
  'remote_bind_failed',
  'generation_stop_failed',
  'local_stream_stop_failed',
  'rtc_leave_failed',
  'session_close_failed',
  'media_cleanup_failed',
  'connect_failed',
  'session_error',
  'pagehide',
] as const
export type XmaxDisconnectReasonCode = typeof XMAX_DISCONNECT_REASON_CODES[number]

/** Map the last operation stage to a stable, actionable terminal reason. */
export function disconnectReasonForStage(stage: StartupStage | undefined): XmaxDisconnectReasonCode {
  switch (stage) {
    case 'media_prepare': return 'media_prepare_failed'
    case 'session_api': return 'session_api_failed'
    case 'rtc_prepare': return 'rtc_prepare_failed'
    case 'camera_capture': return 'camera_capture_failed'
    case 'camera_audio': return 'audio_capture_failed'
    case 'rtc_join': return 'rtc_join_failed'
    case 'camera_ready': return 'camera_ready_failed'
    case 'publish': return 'local_publish_failed'
    case 'outbound_ready': return 'local_outbound_not_ready'
    case 'start_send': return 'generation_start_failed'
    case 'context_set': return 'context_update_failed'
    case 'remote_bind': return 'remote_bind_failed'
    default: return 'connect_failed'
  }
}

const SESSION_BUFFER_LIMIT = 500
type BufferedProbeEvent = { name: XmaxTelemetryEventName; params: XmaxTelemetryParams; bytes: number }
const BUFFER_CRITICAL_EVENTS = new Set<XmaxTelemetryEventName>([
  'sdk_render_output_first_frame', 'sdk_session_error', 'sdk_session_disconnect',
])
const SESSION_SCOPED_EVENTS = new Set<XmaxTelemetryEventName>([
  'sdk_session_connect', 'sdk_session_state_change', 'sdk_session_disconnect',
  'sdk_probe_health',
])

export function diagnosticError(error: unknown): XmaxTelemetryParams {
  try {
    if (!error || typeof error !== 'object') return { error_name: 'UnknownError' }
    const value = error as { name?: unknown; code?: unknown }
    return {
      error_name: typeof value.name === 'string' ? value.name : 'Error',
      error_code: typeof value.code === 'string' || typeof value.code === 'number' ? String(value.code) : undefined,
      ...getErrorDiagnostics(error),
    }
  } catch { return { error_name: 'UnknownError' } }
}

function environment(): XmaxTelemetryParams {
  return {
    visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  }
}

/** One non-blocking diagnostic trace for the complete realtime connection lifecycle. */
export class StartupTrace {
  readonly traceId = telemetryId()
  /** @deprecated Compatibility alias for the earlier startup-only diagnostics. */
  readonly callId = this.traceId
  readonly startedAt = performance.now()
  readonly startedAtEpochMs = Date.now()
  done = false
  outcome?: string
  private readonly times: XmaxTelemetryParams = {}
  private readonly stats: XmaxTelemetryParams = {}
  private readonly terminalContext: XmaxTelemetryParams = {}
  private readonly marks = new Set<string>()
  private readonly rtcStats: RtcStatsAccumulator
  private probeConfig: ResolvedXmaxProbeConfig
  private readonly bufferedEvents: BufferedProbeEvent[] = []
  private buffering: boolean
  private retained = true
  private stage: StartupStage = 'media_prepare'
  private stageStartedAt = this.startedAt
  private seq = 0
  private connectedAt?: number
  private firstFrameAt?: number
  private generationSentAt?: number
  private generationAckAt?: number
  private generationId?: string
  private rtcSamples = 0
  private periodicTimer?: ReturnType<typeof setTimeout>
  private snapshotTimer?: ReturnType<typeof setTimeout>
  private snapshotReason?: string
  private readonly unregisterSnapshot: () => void
  private bufferedBytes = 0
  private bufferedDrops = 0
  private criticalFlushes = 0
  private errorRecorded = false
  private terminalFailure?: XmaxTelemetryParams
  private backgroundSeen = false
  private pageHiding = false
  private readonly onVisibility = () => {
    if (document.visibilityState !== 'hidden') return
    this.backgroundSeen = true
    this.recordRtcSnapshot('page_hidden')
    flushTelemetry(true)
  }
  private readonly onPageHide = () => {
    this.pageHiding = true
    if (!this.done) this.finish('abandoned', {
      failure_reason: 'pagehide',
      close_mode: 'pagehide_beacon',
      page_lifecycle: true,
      server_close_attempted: false,
      server_close_confirmed: false,
    })
    else flushTelemetry(true)
  }

  constructor(private readonly context: XmaxTelemetryParams, private readonly localProbe?: XmaxProbeOptions) {
    this.probeConfig = resolveProbeConfig(localProbe)
    this.rtcStats = new RtcStatsAccumulator(this.probeConfig.windowDurationMs)
    this.unregisterSnapshot = registerTelemetrySnapshot(this.traceId, generationId =>
      generationId === (this.generationId ?? null) ? this.snapshotParams('report') : null,
    )
    this.buffering = this.probeConfig.samplingRate < 1
    this.retained = !this.buffering
    this.backgroundSeen = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    this.schedulePeriodicSnapshot()
    if (typeof document !== 'undefined') document.addEventListener?.('visibilitychange', this.onVisibility)
    if (typeof window !== 'undefined') window.addEventListener?.('pagehide', this.onPageHide)
  }

  get params(): XmaxTelemetryParams {
    return { ...this.context, trace_id: this.traceId, call_id: this.traceId }
  }

  eventContext(): XmaxTelemetryParams {
    return { ...this.params, offset_ms: this.offset(), event_seq: this.seq + 1 }
  }

  offset(at = performance.now()): number { return Math.round(at - this.startedAt) }

  get currentStage(): StartupStage { return this.stage }

  /** @internal Immutable copy for the final public disconnect callback. */
  get terminalFailureDetails(): Readonly<XmaxTelemetryParams> | undefined {
    return this.terminalFailure ? { ...this.terminalFailure } : undefined
  }

  get rtcStatsEnabled(): boolean { return !this.done && this.probeConfig.rtcStatsEnabled }

  rtcEvidence(): XmaxTelemetryParams {
    const evidence: XmaxTelemetryParams = {}
    for (const key of ['up_fps', 'up_bitrate_kbps', 'down_receive_fps', 'down_decode_fps', 'down_render_fps', 'down_bitrate_kbps']) {
      if (typeof this.stats[key] === 'number') evidence[key] = this.stats[key]
    }
    const at = this.offset()
    for (const direction of ['up', 'down']) {
      const observed = this.stats[`${direction}_stats_at_ms`]
      if (typeof observed === 'number') evidence[`${direction}_stats_age_ms`] = Math.max(0, at - observed)
    }
    return evidence
  }

  private schedulePeriodicSnapshot(): void {
    this.periodicTimer = telemetryTimer(() => {
      if (this.done) return
      try {
        this.probeConfig = resolveProbeConfig(this.localProbe)
        this.rtcStats.setWindowDuration(this.probeConfig.windowDurationMs)
        this.recordRtcSnapshot('periodic')
      } catch { /* Optional diagnostics cannot interrupt the session. */ }
      this.schedulePeriodicSnapshot()
    }, 2000)
  }

  setContext(params: XmaxTelemetryParams): void { if (!this.done) Object.assign(this.context, params) }

  setTerminalContext(params: XmaxTelemetryParams): void {
    if (!this.done) Object.assign(this.terminalContext, params)
  }

  record(name: XmaxTelemetryEventName, params: XmaxTelemetryParams = {}): void {
    try {
      if (this.done) return
      if ((name === 'sdk_rtc_connection_state_change' && ['1', '4', '6'].includes(String(params.state)))
        || name === 'sdk_rtc_token_state_change'
        || (name === 'sdk_api_request' && params.ok === false)
        || (name === 'sdk_rtc_teardown' && params.outcome === 'error')) {
        this.retainFullSession()
      }
      const event = {
        ...this.context,
        ...(SESSION_SCOPED_EVENTS.has(name) ? { generation_id: null } : {}),
        trace_id: this.traceId,
        call_id: this.traceId,
        trace_start_time_ms: this.startedAtEpochMs,
        event_seq: ++this.seq,
        offset_ms: this.offset(),
        event_time_ms: Date.now(),
        ...environment(),
        background_seen: this.backgroundSeen,
        ...params,
      }
      if (this.buffering) this.bufferEvent(name, event)
      else emitTelemetry(name, event)
    } catch {
      // Probe failures must never escape into the SDK business path.
    }
  }

  private bufferEvent(name: XmaxTelemetryEventName, params: XmaxTelemetryParams): void {
    const bytes = JSON.stringify(params).length * 2
    while (this.bufferedEvents.length && (this.bufferedBytes + bytes > 120_000 || this.bufferedEvents.length >= SESSION_BUFFER_LIMIT)) {
      const index = this.bufferedEvents.findIndex(event => !BUFFER_CRITICAL_EVENTS.has(event.name))
      if (index < 0) { this.bufferedDrops++; return }
      const [removed] = this.bufferedEvents.splice(index, 1)
      this.bufferedBytes -= removed.bytes
      this.bufferedDrops++
    }
    if (bytes > 120_000) { this.bufferedDrops++; return }
    this.bufferedBytes += bytes
    this.bufferedEvents.push({ name, params, bytes })
  }

  private retainFullSession(): void {
    if (!this.buffering) return
    this.buffering = false
    this.retained = true
    this.bufferedEvents.splice(0).forEach(event => emitTelemetry(event.name, event.params))
    this.bufferedBytes = 0
    if (this.bufferedDrops) this.record('sdk_probe_buffer_overflow', { dropped_count: this.bufferedDrops, queue_limit: SESSION_BUFFER_LIMIT, buffer_limit_bytes: 120_000 })
  }

  enter(stage: StartupStage): void {
    if (this.done) return
    this.stage = stage
    this.stageStartedAt = performance.now()
  }

  complete(stage: StartupStage, startedAt: number): void {
    if (this.done) return
    this.times[`${stage}_ms`] = Math.round(performance.now() - startedAt)
    if (stage === 'start_send') this.mark('start_send_completed')
  }

  async measure<T>(stage: StartupStage, operation: () => Promise<T>): Promise<T> {
    const start = performance.now()
    this.enter(stage)
    try { return await operation() } finally { this.complete(stage, start) }
  }

  mark(name: string): void {
    if (this.done || this.marks.has(name) || this.marks.size >= 24) return
    this.marks.add(name)
    this.times[`${name}_at_ms`] = this.offset()
  }

  markConnected(): void {
    if (this.done || this.connectedAt !== undefined) return
    this.connectedAt = performance.now()
    this.mark('connected')
    this.record('sdk_session_connect', { connected_ms: this.offset(this.connectedAt) })
  }

  setGeneration(generationId: string): void {
    if (this.done) return
    this.generationId = generationId
    this.generationSentAt = undefined
    this.generationAckAt = undefined
    this.setContext({ generation_id: generationId })
  }

  clearGeneration(generationId?: string | null): void {
    if (this.done || (generationId !== undefined && generationId !== this.generationId)) return
    this.generationId = undefined
    this.generationSentAt = undefined
    this.generationAckAt = undefined
    this.setContext({ generation_id: null })
  }

  generationSent(params: XmaxTelemetryParams = {}): void {
    if (this.done) return
    if (params.generation_id === undefined || params.generation_id === this.generationId) {
      this.generationSentAt = performance.now()
    }
    this.record('sdk_generation_request_sent', { generation_id: this.generationId, ...params })
  }

  generationAckReceived(generationId: string, params: XmaxTelemetryParams = {}): void {
    if (
      this.done
      || this.generationAckAt !== undefined
      || this.generationId !== generationId
    ) return
    this.generationAckAt = performance.now()
    this.mark('generation_ack')
    this.record('sdk_generation_ack_received', {
      generation_id: generationId,
      ack_latency_ms: this.generationSentAt === undefined
        ? undefined
        : Math.round(this.generationAckAt - this.generationSentAt),
      ...params,
    })
  }

  firstFrame(params: XmaxTelemetryParams = {}): void {
    if (this.done || this.firstFrameAt !== undefined) return
    this.firstFrameAt = performance.now()
    this.enter('streaming')
    if (this.probeConfig.e2eThresholdMs !== undefined && this.offset(this.firstFrameAt) >= this.probeConfig.e2eThresholdMs) {
      this.retainFullSession()
    }
    this.mark('first_frame_signal')
    this.record('sdk_render_output_first_frame', {
      ...this.times,
      generation_id: this.generationId,
      e2e_duration_ms: this.offset(this.firstFrameAt),
      generation_duration_ms: this.generationSentAt === undefined ? undefined : Math.round(this.firstFrameAt - this.generationSentAt),
      first_frame_source: this.stats.first_frame_source,
      sei_matched: this.stats.sei_matched_at_frame,
      ...params,
    })
    this.recordRtcSnapshot('first_frame')
    if (this.pageHiding) flushTelemetry(true)
    else this.flushCritical()
  }

  updateStats(params: XmaxTelemetryParams): void {
    if (this.done) return
    Object.assign(this.stats, params)
    for (const key of ['up_rtt_ms', 'down_rtt_ms', 'up_loss_rate', 'down_loss_rate']) {
      const value = params[key]
      if (typeof value === 'number') this.stats[`${key}_max`] = Math.max(Number(this.stats[`${key}_max`] ?? 0), value)
    }
  }

  recordRtcStats(values: Partial<Record<RtcMetricName, number>>, stalls?: { count?: number; durationMs?: number }): void {
    if (!this.rtcStatsEnabled) return
    const at = this.offset()
    this.rtcStats.add(at, values, stalls)
    this.rtcSamples++
  }

  recordRtcSnapshot(reason: string): void {
    if (!this.rtcStatsEnabled) return
    this.snapshotReason = reason
    if (this.snapshotTimer !== undefined) return
    const capture = () => {
      this.snapshotTimer = undefined
      try {
        if (this.done && !this.retained) return
        const snapshot = this.snapshotParams(this.snapshotReason ?? reason, true)
        if (!snapshot) return
        const params = {
          ...this.params, trace_start_time_ms: this.startedAtEpochMs,
          offset_ms: this.offset(), event_time_ms: Date.now(), event_seq: ++this.seq,
          ...environment(), ...snapshot,
        }
        if (this.buffering && !this.done) this.bufferEvent('sdk_rtc_stats_snapshot', params)
        else emitTelemetry('sdk_rtc_stats_snapshot', params)
      } catch { /* Snapshot work is isolated from media and session callbacks. */ }
      finally { if (this.done) this.rtcStats.clear() }
    }
    if (this.pageHiding) capture()
    else this.snapshotTimer = telemetryTimer(capture, 0)
  }

  private snapshotParams(reason: string, allowDone = false): XmaxTelemetryParams | null {
    if ((!allowDone && this.done) || !this.probeConfig.rtcStatsEnabled) return null
    const at = this.offset()
    const snapshot = this.rtcStats.snapshot(at)
    if (!snapshot) return null
    return {
      ...snapshot,
      ...this.stats,
      generation_id: this.generationId ?? null,
      up_stats_age_ms: typeof this.stats.up_stats_at_ms === 'number' ? Math.max(0, at - this.stats.up_stats_at_ms) : undefined,
      down_stats_age_ms: typeof this.stats.down_stats_at_ms === 'number' ? Math.max(0, at - this.stats.down_stats_at_ms) : undefined,
      snapshot_reason: reason,
      current_stage: this.stage,
      current_stage_elapsed_ms: Math.round(performance.now() - this.stageStartedAt),
    }
  }

  recordError(params: XmaxTelemetryParams): void {
    if (this.done || this.errorRecorded) return
    this.errorRecorded = true
    this.terminalFailure = { ...params }
    this.retainFullSession()
    this.recordRtcSnapshot('error')
    this.record('sdk_session_error', {
      ...this.times,
      current_stage: this.stage,
      error_code: params.error_code ?? params.failure_reason ?? 'UNKNOWN_ERROR',
      ...params,
    })
    if (this.pageHiding) flushTelemetry(true)
    else this.flushCritical()
  }

  private flushCritical(): void {
    if (this.criticalFlushes >= 3) return
    this.criticalFlushes++
    flushTelemetry()
  }

  finish(outcome: 'success' | 'error' | 'cancelled' | 'connected' | 'abandoned' | 'unobserved', params: XmaxTelemetryParams = {}): void {
    try { this.finishInternal(outcome, params) } catch { this.disposeAfterProbeFailure(outcome) }
  }

  private finishInternal(outcome: 'success' | 'error' | 'cancelled' | 'connected' | 'abandoned' | 'unobserved', params: XmaxTelemetryParams): void {
    if (this.done) return
    if (outcome === 'connected') { this.markConnected(); return }
    if (outcome === 'success') { this.firstFrame(params); return }
    const requestedReason = params.failure_reason
    const recordedReason = this.terminalFailure?.failure_reason
    const promoteRecordedFailure = (
      (outcome === 'cancelled'
        && (requestedReason === 'client' || requestedReason === 'client_disconnect'
          || requestedReason === 'client_background_timeout' || requestedReason === 'client_session_replaced'
          || requestedReason === 'client_context_disposed' || requestedReason === 'pagehide'))
      || (outcome === 'error' && requestedReason === 'session_error')
    )
      && typeof recordedReason === 'string'
    const terminalParams = promoteRecordedFailure
      ? { ...params, ...this.terminalFailure, disconnect_trigger: `${requestedReason === 'client' ? 'client_disconnect' : requestedReason}_after_error` }
      : params
    if (outcome !== 'cancelled' || promoteRecordedFailure) this.retainFullSession()
    if (outcome === 'error') this.recordError(terminalParams)
    if (outcome !== 'unobserved') {
      this.recordRtcSnapshot('disconnect')
      this.record('sdk_session_disconnect', {
        ...this.times,
        outcome,
        reason_code: terminalParams.failure_reason ?? outcome,
        alive_ms: this.offset(),
        is_abnormal: outcome !== 'cancelled' || promoteRecordedFailure,
        connection_established: this.connectedAt !== undefined,
        session_created: typeof this.context.session_id === 'string',
        had_first_frame: this.firstFrameAt !== undefined,
        current_stage: this.stage,
        ...this.terminalContext,
        ...terminalParams,
      })
    }
    this.record('sdk_probe_health', {
      ...telemetryHealth(),
      rtc_samples: this.rtcSamples,
      trace_event_count: this.seq,
      buffered_event_count: this.bufferedEvents.length,
      sampling_rate: this.probeConfig.samplingRate,
      connection_established: this.connectedAt !== undefined,
      session_created: typeof this.context.session_id === 'string',
      had_first_frame: this.firstFrameAt !== undefined,
      critical_flush_count: Math.min(3, this.criticalFlushes + 1),
      finish_reason: terminalParams.failure_reason ?? outcome,
      report_bytes: telemetryReportedBytes(this.traceId),
      session_buffer_dropped: this.bufferedDrops,
    })
    if (this.buffering) {
      if (Math.random() < this.probeConfig.samplingRate) this.retainFullSession()
      else {
        this.bufferedEvents.length = 0
        this.retained = false
      }
    }
    this.done = true
    this.unregisterSnapshot()
    if (this.snapshotTimer === undefined) this.rtcStats.clear()
    clearTimeout(this.periodicTimer)
    this.outcome = outcome
    if (typeof document !== 'undefined') document.removeEventListener?.('visibilitychange', this.onVisibility)
    if (typeof window !== 'undefined') window.removeEventListener?.('pagehide', this.onPageHide)
    if (this.retained) {
      if (this.pageHiding) flushTelemetry(true)
      else this.flushCritical()
    }
  }

  private disposeAfterProbeFailure(outcome: string): void {
    this.unregisterSnapshot()
    clearTimeout(this.snapshotTimer)
    this.rtcStats.clear()
    clearTimeout(this.periodicTimer)
    this.done = true
    this.outcome = outcome
    try { if (typeof document !== 'undefined') document.removeEventListener?.('visibilitychange', this.onVisibility) } catch { /* fail silent */ }
    try { if (typeof window !== 'undefined') window.removeEventListener?.('pagehide', this.onPageHide) } catch { /* fail silent */ }
  }
}

export function beginStartup(enabled: boolean | undefined, params: XmaxTelemetryParams, localProbe?: XmaxProbeOptions): StartupTrace | undefined {
  if (enabled === false || !telemetryEnabled()) return undefined
  try { return new StartupTrace(params, localProbe) } catch { return undefined }
}
